import { ScheduleDefinition } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_definitions.pb';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Modal, ModalControlModalProps, NakedUl, Text } from '@frontend/design-system';
import { getBookableEntityInfo } from '../../../../hooks/settings/use-availability-rule-form';
import { getAvailabilityRecurrenceText } from '../../helpers';

type Props = {
  availabilityRule: ScheduleDefinition | null;
  modalProps: ModalControlModalProps;
  onConfirm: () => void;
};

export const DeleteConfirmationModal = ({ availabilityRule, modalProps, onConfirm }: Props) => {
  const { t } = useTranslation('schedule-settings');

  const recurrenceText = availabilityRule?.rRule
    ? getAvailabilityRecurrenceText(availabilityRule.rRule, availabilityRule?.startDate)
    : undefined;

  const start = availabilityRule?.startTime ? dayjs(availabilityRule.startTime, 'HH:mm').format('h:mm A') : '';
  const end = availabilityRule?.endTime ? dayjs(availabilityRule.endTime, 'HH:mm').format('h:mm A') : '';
  const timeLabel = start && end ? `${start} - ${end}` : start || end || '-';

  const services = availabilityRule?.bookableAppointmentTypes ?? [];
  const MAX_VISIBLE_SERVICES = 1;
  const visibleService = services[0]?.name ?? '';
  const extraCount = services.length - MAX_VISIBLE_SERVICES;
  const servicesLabel =
    services.length === 0
      ? '-'
      : extraCount > 0
      ? t('{{name}}, +{{count}} services', { name: visibleService, count: extraCount })
      : visibleService;
  const { serviceableProviderId } = getBookableEntityInfo(availabilityRule?.bookableEntities);
  const provider = availabilityRule?.bookableEntities.find((entity) => entity.resourceId === serviceableProviderId);

  return (
    <Modal {...modalProps} maxWidth={400}>
      <Modal.Header onClose={modalProps.onClose}>{t('Delete Availability Rule?')}</Modal.Header>
      <Modal.Body
        css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3), paddingRight: theme.spacing(6) }}
      >
        <Text>{t('Are you sure you want to delete this availability rule?')}</Text>
        {availabilityRule && (
          <NakedUl css={{ listStyleType: 'initial', paddingLeft: theme.spacing(2.5) }}>
            {availabilityRule.displayName && <Text as='li'>{availabilityRule.displayName}</Text>}
            {availabilityRule?.recurrenceType && <Text as='li'>{availabilityRule.recurrenceType}</Text>}
            {recurrenceText && recurrenceText !== '-' && <Text as='li'>{recurrenceText}</Text>}
            {availabilityRule?.startTime && <Text as='li'>{timeLabel}</Text>}
            {provider?.resourceDisplayName && <Text as='li'>{provider.resourceDisplayName}</Text>}
            {services.length > 0 && <Text as='li'>{servicesLabel}</Text>}
          </NakedUl>
        )}
        <Text>{t("This will remove this availability rule but won't affect existing appointments.")}</Text>
      </Modal.Body>
      <Modal.Footer
        primary={{ label: t('Delete'), onClick: onConfirm, destructive: true }}
        secondary={{ label: t('Cancel'), onClick: modalProps.onClose }}
      />
    </Modal>
  );
};
